import React, { useState, useEffect, useRef } from 'react';
import {
    CreditCard, CheckCircle, AlertTriangle, QrCode, Loader2, Copy,
    Users, ArrowUpCircle, Building, Save, CalendarClock, History, Shield,
    Search, MapPin, Phone, Mail, FileText, Hash, Image as ImageIcon
} from 'lucide-react';

// --- SERVIÇOS LOCAIS ---
import api from '../services/api';
import { socket } from '../services/socket';
import Modal from '../components/Modal';

// Preços simulados (Você pode ajustar ou trazer do backend)


// Calcula pró-rata localmente para preview no modal
const calcLocalProRata = (nextDueDate, diffUsers) => {
    const pricePerExtra = 99.90;
    const now = new Date();
    const due = nextDueDate ? new Date(nextDueDate) : (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d; })();
    const msRemaining = due - now;
    const daysRemaining = Math.max(1, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
    const factor = Math.min(1, daysRemaining / 30);
    const proRataAmount = Math.max(1.00, parseFloat((diffUsers * pricePerExtra * factor).toFixed(2)));
    const fullMonthly = diffUsers * pricePerExtra;
    return { daysRemaining, factor, proRataAmount, fullMonthly };
};

const AssinaturaScreen = ({ currentUser }) => {
    // --- ESTADOS ---
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    // Dados de Pagamento (Pix)
    const [paymentData, setPaymentData] = useState(null); // { qrCodeBase64, copyPaste }

    // Modais
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [desiredUsers, setDesiredUsers] = useState(3);

    // ==================================================================================
    // 1. INICIALIZAÇÃO E SOCKET
    // ==================================================================================
    useEffect(() => {
        fetchCompanyData();

        // Escuta confirmação de pagamento em tempo real
        socket.on('payment_confirmed', () => {
            alert("Pagamento confirmado! Sua assinatura está ativa.");
            setPaymentData(null); // Fecha o QR Code
            fetchCompanyData(); // Recarrega dados
        });

        // Escuta mudanças no plano
        socket.on('company_updated', (data) => {
            setCompany(prev => ({ ...prev, ...data }));
        });

        return () => {
            socket.off('payment_confirmed');
            socket.off('company_updated');
        };
    }, []);

    const fetchCompanyData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/company/status');
            setCompany(res.data);
            setDesiredUsers(res.data.usersLimit || 3);
        } catch (error) {
            console.error("Erro ao carregar assinatura:", error);
        } finally {
            setLoading(false);
        }
    };

    // ==================================================================================
    // 2. AÇÕES FINANCEIRAS
    // ==================================================================================

    // Gerar Pix (Renovação ou Upgrade)
    const handleGeneratePix = async () => {
        setProcessing(true);
        try {
            // Chama rota do backend que integra com (MercadoPago/Asaas/EFI)
            // Chama rota do backend que integra com (MercadoPago/Asaas/EFI)
            const users = company.usersLimit || 3;
            const amount = 199.90 + (Math.max(0, users - 3) * 99.90);

            const res = await api.post('/subscription/pay', {
                amount: amount.toFixed(2),
                description: `Mensalidade WA-Desk - ${users} usuários`
            });

            // O backend deve retornar { qrCodeBase64: '...', copyPaste: '...' }
            setPaymentData(res.data);
        } catch (error) {
            console.error(error);
            alert("Erro ao gerar Pix. Verifique se o módulo de pagamento está configurado no servidor.");
        } finally {
            setProcessing(false);
        }
    };



    // Confirmar mudança de plano
    const handleUpgradeConfirm = async () => {
        setIsUpgradeModalOpen(false);
        // Gera um Pix com o valor do novo plano (pró-rata ou cheio, dependendo da sua regra de negócio)
        setProcessing(true);
        try {
            const res = await api.post('/subscription/upgrade', { newLimit: desiredUsers });

            if (res.data.paymentRequired) {
                setPaymentData(res.data.paymentData);
            } else {
                alert("Plano alterado com sucesso!");
                fetchCompanyData();
            }
        } catch (error) {
            alert("Erro ao alterar plano.");
        } finally {
            setProcessing(false);
        }
    };

    const copyToClipboard = () => {
        if (paymentData?.copyPaste) {
            navigator.clipboard.writeText(paymentData.copyPaste);
            alert("Código Pix copiado!");
        }
    };

    // ==================================================================================
    // 3. RENDERIZAÇÃO
    // ==================================================================================

    if (loading) return <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin text-blue-600" /></div>;
    if (!company) return <div className="p-8 text-center text-gray-500">Erro ao carregar dados da empresa.</div>;

    const isOverdue = company.status === 'overdue' || company.status === 'suspended';
    // Nova Lógica de Preço: Base 199.90 (3 users) + 99.90 por extra
    const baseUsers = 3;
    const basePrice = 199.90;
    const extraPrice = 99.90;
    const extraUsers = Math.max(0, (company.usersLimit || 3) - baseUsers);
    const currentPrice = basePrice + (extraUsers * extraPrice);

    return (
        <div className="p-6 h-full bg-gray-50 overflow-y-auto font-sans">
            <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <CreditCard className="mr-3 text-blue-600" /> Assinatura e Planos
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* COLUNA ESQUERDA: STATUS E PAGAMENTO */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Card de Status */}
                    <div className={`p-6 rounded-xl border shadow-sm relative overflow-hidden flex flex-col md:flex-row justify-between items-center
                        ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}
                    `}>
                        <div className="z-10 mb-4 md:mb-0">
                            <h2 className={`text-lg font-bold flex items-center ${isOverdue ? 'text-red-800' : 'text-gray-800'}`}>
                                {isOverdue ? <AlertTriangle className="mr-2" /> : <CheckCircle className="mr-2 text-green-500" />}
                                Status: {isOverdue ? 'Pagamento Pendente' : 'Assinatura Ativa'}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {isOverdue
                                    ? 'Regularize para evitar o bloqueio do sistema.'
                                    : `Próxima renovação em: ${company.next_due_date ? new Date(company.next_due_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Data não definida'}`
                                }
                            </p>
                        </div>

                        {!paymentData && (
                            <button
                                onClick={handleGeneratePix}
                                disabled={processing}
                                className={`z-10 px-6 py-3 rounded-lg font-bold shadow-lg flex items-center ${isOverdue ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' : 'bg-green-600 text-white hover:bg-green-700'}`}
                            >
                                {processing ? <Loader2 className="animate-spin mr-2" /> : <QrCode className="mr-2" />}
                                {isOverdue ? 'Pagar Pendência' : 'Pagar Assinatura'}
                            </button>
                        )}
                    </div>

                    {/* Área do PIX (Só aparece se tiver gerado) */}
                    {paymentData && (
                        <div className="bg-white p-6 rounded-xl border-2 border-blue-500 shadow-lg animate-in fade-in slide-in-from-top-4">
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-bold text-gray-800">Pagamento via Pix</h3>
                                <p className="text-gray-500">Escaneie o QR Code ou copie o código abaixo.</p>
                            </div>

                            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                                {/* QR Code Imagem */}
                                <div className="bg-white p-2 border rounded-lg shadow-sm">
                                    {paymentData.qrCodeBase64 ? (
                                        <img src={`data:image/png;base64,${paymentData.qrCodeBase64}`} alt="Pix QR Code" className="w-48 h-48" />
                                    ) : (
                                        <div className="w-48 h-48 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                            [QR Code Mock]
                                        </div>
                                    )}
                                </div>

                                {/* Copia e Cola */}
                                <div className="w-full max-w-md space-y-3">
                                    <div className="relative">
                                        <textarea
                                            readOnly
                                            value={paymentData.copyPaste || "00020126360014BR.GOV.BCB.PIX..."}
                                            className="w-full p-3 pr-12 bg-gray-50 border rounded-lg text-xs font-mono text-gray-600 h-24 resize-none focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                        <button
                                            onClick={copyToClipboard}
                                            className="absolute top-2 right-2 p-2 bg-white rounded shadow-sm hover:bg-gray-100 text-blue-600"
                                            title="Copiar Código"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-center text-gray-400 flex items-center justify-center">
                                        <CalendarClock className="w-3 h-3 mr-1" />
                                        Expira em 30 minutos
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 text-center">
                                <button
                                    onClick={() => setPaymentData(null)}
                                    className="text-red-500 hover:text-red-700 text-sm font-medium underline"
                                >
                                    Cancelar Pagamento
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Dados de Cadastro da Empresa */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-5 flex items-center">
                            <Building className="mr-2 text-gray-400" /> Dados da Empresa / Responsável
                        </h3>

                        {/* CARD RESUMO DADOS DA EMPRESA */}
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xl uppercase shadow-sm border border-blue-200">
                                {company.name ? company.name.substring(0, 2) : 'EM'}
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-gray-800">{company.name}</h4>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold mt-1 ${company.tipo_pessoa === 'PF' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {company.tipo_pessoa === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">Documento ({company.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'})</p>
                                    <p className="text-sm font-medium text-gray-800">{company.tipo_pessoa === 'PF' ? (company.cpf || 'Não informado') : (company.cnpj || 'Não informado')}</p>
                                </div>
                                {company.tipo_pessoa === 'PJ' && (
                                    <div>
                                        <p className="text-xs font-bold text-gray-400 uppercase">Inscrição Estadual</p>
                                        <p className="text-sm font-medium text-gray-800">{company.ie || 'ISENTO'}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">Telefone</p>
                                    <p className="text-sm font-medium text-gray-800 flex items-center">
                                        <Phone className="w-3 h-3 mr-1 text-gray-400" /> {company.phone || 'Não informado'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">E-mail Financeiro</p>
                                    <p className="text-sm font-medium text-gray-800 flex items-center">
                                        <Mail className="w-3 h-3 mr-1 text-gray-400" /> {company.financial_email || company.owner_email}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4 md:border-l md:border-gray-200 md:pl-6">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase flex items-center">
                                        <MapPin className="w-3 h-3 mr-1" /> Endereço
                                    </p>
                                    {company.address?.logradouro ? (
                                        <div className="text-sm font-medium text-gray-800 mt-1 space-y-1">
                                            <p>{company.address.logradouro}, {company.address.numero} {company.address.complemento && `- ${company.address.complemento}`}</p>
                                            <p>{company.address.bairro}</p>
                                            <p>{company.address.cidade} - {company.address.uf}</p>
                                            <p className="text-gray-500">CEP: {company.address.cep}</p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 mt-1 italic">Endereço não cadastrado.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* COLUNA DIREITA: PLANO */}
                <div className="space-y-6">
                    <div className="bg-blue-600 p-6 rounded-xl shadow-lg text-white relative overflow-hidden">
                        <div className="relative z-10">
                            <p className="text-blue-200 text-xs font-bold uppercase tracking-wider mb-1">Plano Atual</p>
                            <h2 className="text-3xl font-extrabold mb-1">{company.usersLimit || 3} Usuários</h2>
                            <p className="text-blue-100 text-sm mb-6">Limite de conexões simultâneas.</p>

                            <div className="flex items-baseline mb-6">
                                <span className="text-4xl font-bold">R$ {currentPrice.toFixed(2)}</span>
                                <span className="text-blue-200 ml-2">/mês</span>
                            </div>

                            <button
                                onClick={() => setIsUpgradeModalOpen(true)}
                                className="w-full bg-white text-blue-600 py-3 rounded-lg font-bold hover:bg-blue-50 flex items-center justify-center shadow-md"
                            >
                                <ArrowUpCircle className="w-5 h-5 mr-2" />
                                Fazer Upgrade
                            </button>
                        </div>
                        {/* Decor */}
                        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full"></div>
                    </div>

                    {/* Histórico Simples */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                            <History className="mr-2 text-gray-400" /> Histórico Recente
                        </h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-sm border-b pb-2">
                                <span className="text-gray-600">Mensalidade (Dez)</span>
                                <span className="font-bold text-green-600">Pago</span>
                            </div>
                            <div className="flex justify-between items-center text-sm border-b pb-2">
                                <span className="text-gray-600">Mensalidade (Nov)</span>
                                <span className="font-bold text-green-600">Pago</span>
                            </div>
                            {isOverdue && (
                                <div className="flex justify-between items-center text-sm border-b pb-2 bg-red-50 p-1 rounded">
                                    <span className="text-red-600 font-bold">Mensalidade (Jan)</span>
                                    <span className="font-bold text-red-600">Pendente</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs text-gray-500 flex items-start">
                        <Shield className="w-8 h-8 mr-2 text-gray-400 flex-shrink-0" />
                        <p>
                            Seus dados de pagamento são processados de forma segura por nosso gateway.
                            Nenhuma informação de cartão é armazenada localmente.
                        </p>
                    </div>
                </div>
            </div>

            {/* MODAL DE UPGRADE */}
            {(() => {
                const currentUsers = company?.usersLimit || 3;
                const diffUsers = Math.max(0, desiredUsers - currentUsers);
                const newMonthlyPrice = 199.90 + ((desiredUsers - 3) * 99.90);
                const proRata = diffUsers > 0 ? calcLocalProRata(company?.next_due_date, diffUsers) : null;
                const confirmLabel = proRata
                    ? `Confirmar e Pagar R$ ${proRata.proRataAmount.toFixed(2)}`
                    : desiredUsers < currentUsers
                        ? 'Confirmar Downgrade'
                        : 'Confirmar';

                return (
                    <Modal
                        isOpen={isUpgradeModalOpen}
                        onClose={() => setIsUpgradeModalOpen(false)}
                        title="Alterar Plano"
                        confirmText={confirmLabel}
                        onConfirm={handleUpgradeConfirm}
                        isLoading={processing}
                    >
                        <div className="py-4">
                            <div className="grid grid-cols-1 gap-6 mb-6">
                                <div className="bg-white p-6 rounded-lg border-2 border-blue-100 shadow-sm flex flex-col items-center">
                                    <h3 className="text-lg font-bold text-gray-700 mb-2">Quantos usuários você precisa?</h3>

                                    <div className="flex items-center gap-4 mb-4">
                                        <button
                                            onClick={() => setDesiredUsers(Math.max(3, desiredUsers - 1))}
                                            className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 font-bold text-xl hover:bg-gray-200 transition-colors flex items-center justify-center"
                                        >
                                            -
                                        </button>

                                        <div className="text-center w-32">
                                            <span className="text-4xl font-extrabold text-blue-600">{desiredUsers}</span>
                                            <p className="text-xs uppercase font-bold text-gray-400">Usuários</p>
                                        </div>

                                        <button
                                            onClick={() => setDesiredUsers(desiredUsers + 1)}
                                            className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition-colors flex items-center justify-center"
                                        >
                                            +
                                        </button>
                                    </div>

                                    <div className="w-full h-px bg-gray-100 my-2"></div>

                                    {/* Resumo de preço */}
                                    <div className="w-full mt-3 space-y-2">
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>Valor mensal novo plano:</span>
                                            <span className="font-semibold">R$ {newMonthlyPrice.toFixed(2)}/mês</span>
                                        </div>

                                        {proRata && (
                                            <>
                                                <div className="flex justify-between text-sm text-gray-600">
                                                    <span>Dias restantes no ciclo:</span>
                                                    <span className="font-semibold">{proRata.daysRemaining} dia(s)</span>
                                                </div>
                                                <div className="w-full h-px bg-gray-100"></div>
                                                <div className="flex justify-between text-base font-bold text-blue-700">
                                                    <span>Cobrança imediata (pró-rata):</span>
                                                    <span>R$ {proRata.proRataAmount.toFixed(2)}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {proRata ? (
                                <div className="bg-blue-50 p-3 rounded text-sm text-blue-800 border border-blue-200">
                                    <p className="font-bold">Como funciona o pró-rata?</p>
                                    <p>Você paga hoje apenas pelo tempo restante até o próximo vencimento ({proRata.daysRemaining} dia(s)).
                                    A partir do próximo mês, sua fatura será de <strong>R$ {newMonthlyPrice.toFixed(2)}/mês</strong>.</p>
                                </div>
                            ) : (
                                <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-800 border border-yellow-200">
                                    <p className="font-bold">Nota:</p>
                                    <p>Ao diminuir o plano, a mudança será aplicada imediatamente. Não haverá reembolso do período já pago.</p>
                                </div>
                            )}
                        </div>
                    </Modal>
                );
            })()}
        </div>
    );
};

export default AssinaturaScreen;